import api from './api';

// The shared axios instance defaults to a 30s timeout, which is too short for
// large catalog exports: a streamed ZIP of images (up to 200 items,
// originals + web renditions, fetched sequentially from S3 on the backend),
// or the PDF data endpoint (uncapped since #1208; full multi-table facet
// scans plus a sequential per-item provenance fetch). Regression fix for
// #1267 — "Image export didn't work" (surfaced as "timeout of 30000ms
// exceeded"); the PDF-data endpoint shares the identical failure mode.
const EXPORT_TIMEOUT_MS = 180000;

export interface ExportFieldOption {
  name: string;
  label: string;
  category: string;
}

export interface ExportFieldsResponse {
  fields: ExportFieldOption[];
}

export const getExportFields = async (): Promise<ExportFieldOption[]> => {
  const response = await api.get('/api/export/fields');
  return (response.data as ExportFieldsResponse).fields;
};

export const exportCsv = async (
  fields: string[],
  collectionId?: string,
  ownerId?: string,
  selectedIds?: string[]
): Promise<void> => {
  const url = ownerId
    ? `/api/export/csv?owner_id=${encodeURIComponent(ownerId)}`
    : '/api/export/csv';
  const body: Record<string, unknown> = {
    fields,
    collection_id: collectionId,
    ...(selectedIds && selectedIds.length > 0
      ? { selected_ids: selectedIds }
      : {}),
  };
  const response = await api.post(url, body, {
    responseType: 'blob',
  } as Record<string, unknown>);
  triggerDownload(response.data as Blob, 'catalog-export.csv', 'text/csv');
};

export const exportImages = async (
  collectionId?: string,
  includeOriginals = true,
  includeWeb = true,
  ownerId?: string,
  selectedIds?: string[]
): Promise<void> => {
  const url = ownerId
    ? `/api/export/images?owner_id=${encodeURIComponent(ownerId)}`
    : '/api/export/images';
  const body: Record<string, unknown> = {
    collection_id: collectionId,
    include_originals: includeOriginals,
    include_web: includeWeb,
    ...(selectedIds && selectedIds.length > 0
      ? { selected_ids: selectedIds }
      : {}),
  };
  const response = await api.post(url, body, {
    responseType: 'blob',
    timeout: EXPORT_TIMEOUT_MS,
  } as Record<string, unknown>);
  triggerDownload(
    response.data as Blob,
    'catalog-images.zip',
    'application/zip'
  );
};

export interface PdfDataItem {
  ali_catalog_number: string;
  title: string;
  creators: string;
  creation_date: string;
  work_type: string;
  techniques: string;
  materials: string;
  media_description: string;
  dimensions: string;
  keywords: string;
  public_notes: string;
  private_notes: string;
  physical_location: string;
  estimated_value: string;
  provenance_records: string;
  editions: string;
  exhibitions: string;
  publications: string;
  custom_catalog_numbers: string;
  alternative_titles: string;
  substrate: string;
  image_url: string | null;
}

export const fetchPdfData = async (
  collectionId?: string,
  includePrivateFields = false,
  ownerId?: string,
  selectedIds?: string[]
): Promise<PdfDataItem[]> => {
  const url = ownerId
    ? `/api/export/pdf-data?owner_id=${encodeURIComponent(ownerId)}`
    : '/api/export/pdf-data';
  const body: Record<string, unknown> = {
    collection_id: collectionId,
    include_private_fields: includePrivateFields,
    ...(selectedIds && selectedIds.length > 0
      ? { selected_ids: selectedIds }
      : {}),
  };
  const response = await api.post(url, body, {
    timeout: EXPORT_TIMEOUT_MS,
  } as Record<string, unknown>);
  return (response.data as { items: PdfDataItem[] }).items;
};

const triggerDownload = (blob: Blob, filename: string, mimeType: string) => {
  const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
